/**
 * Holds the file someone picked before they had an account, across the
 * OAuth redirect that gets them one.
 *
 * The onboarding funnel lets a visitor choose their form first and sign in
 * second, so the file has to survive a full navigation away to the provider
 * and back. `localStorage` cannot carry a `File` — it stringifies to
 * `"[object File]"` — so this uses IndexedDB, which stores Blobs natively
 * and keeps the bytes out of any string quota.
 *
 * Everything here fails soft. A blocked or evicted store (private window,
 * cleared site data, Safari's ITP) means the person re-picks their file,
 * which is a small annoyance; a thrown error here would break the sign-in
 * they are in the middle of.
 */

const DB_NAME = 'formalogix'
const STORE = 'pending-upload'
const KEY = 'current'

/** What the funnel needs to resume where it left off. */
export interface PendingUpload {
  file: File
  /** Which detector the person chose on the pick-a-form step. */
  detectMethod?: 'cv' | 'llm'
  /** The person agreed, on the upload screen, that a model may read this
   *  document. Held with the file because the agreement is given there and
   *  the upload happens a screen later, possibly after registering. */
  aiConsent?: boolean
  /** Epoch ms, so a file forgotten for a day is not silently uploaded later. */
  storedAt: number
  /**
   * How far the analyse step got with this file.
   *
   * `take()` is deliberately non-destructive, so a reload of
   * `/onboarding/analyse` finds the file again — and without this would
   * upload it a second time, creating a second template and a second
   * document, each one billed against the account's page quota. The ids are
   * written as soon as each request answers, and the screen resumes from
   * them instead of starting over.
   */
  run?: PendingRun
}

export interface PendingRun {
  templateId?: string
  documentId?: string
  jobId?: string
}

/** What is actually written: the file taken apart into plain data. */
interface StoredUpload extends Omit<PendingUpload, 'file'> {
  bytes: ArrayBuffer
  name: string
  type: string
  lastModified: number
}

const MAX_AGE_MS = 60 * 60 * 1000

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null)
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, 1)
    } catch {
      return resolve(null)
    }
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })
}

/** FileReader rather than `Blob.arrayBuffer()`, which jsdom lacks. */
function readBytes(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}

function withStore<T>(
  mode: IDBTransactionMode,
  body: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return open().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null)
        try {
          const tx = db.transaction(STORE, mode)
          const request = body(tx.objectStore(STORE))
          request.onsuccess = () => resolve(request.result ?? null)
          request.onerror = () => resolve(null)
          tx.oncomplete = () => db.close()
        } catch {
          resolve(null)
        }
      }),
  )
}

export const pendingUpload = {
  async save(file: File, detectMethod?: 'cv' | 'llm', aiConsent?: boolean): Promise<void> {
    let bytes: ArrayBuffer
    try {
      bytes = await readBytes(file)
    } catch {
      return
    }
    const entry: StoredUpload = {
      bytes,
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
      detectMethod,
      aiConsent,
      storedAt: Date.now(),
    }
    await withStore('readwrite', (store) => store.put(entry, KEY))
  },

  /** The held file, or null when there is none, it is stale, its bytes did
   *  not survive, or the store is unavailable. A stale or unreadable entry is
   *  cleared on the way out, so the person is asked to pick the file again
   *  rather than an empty upload being sent. */
  async take(): Promise<PendingUpload | null> {
    const entry = (await withStore<StoredUpload>('readonly', (store) => store.get(KEY))) ?? null
    if (!entry) return null
    const usable = (entry.bytes?.byteLength ?? 0) > 0
    if (!usable || Date.now() - entry.storedAt > MAX_AGE_MS) {
      await pendingUpload.clear()
      return null
    }
    const { bytes, name, type, lastModified, ...rest } = entry
    return { ...rest, file: new File([bytes], name, { type, lastModified }) }
  },

  /**
   * Records what the run has already created. A store that refuses the write
   * simply means the old behaviour — a reload starts over — which is why
   * nothing here throws.
   */
  async markProgress(run: PendingRun): Promise<void> {
    const entry = (await withStore('readonly', (store) => store.get(KEY))) as StoredUpload | null
    if (!entry) return
    await withStore('readwrite', (store) =>
      store.put({ ...entry, run: { ...entry.run, ...run } }, KEY),
    )
  },

  async clear(): Promise<void> {
    await withStore('readwrite', (store) => store.delete(KEY))
  },
}
